import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { MaintenanceProvider } from './context/MaintenanceContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MaintenanceGuard } from './components/MaintenanceGuard';
import { ScrollToTop } from './components/ScrollToTop';
import { useFavicon } from './hooks/useFavicon';

// Layouts
import { PublicLayout } from './components/layout/PublicLayout';
import { AdminLayout } from './components/layout/AdminLayout';

// Public Pages
import { HomePage } from './pages/public/HomePage';
import { EditionsPage } from './pages/public/EditionsPage';
import { PastEditionsPage } from './pages/public/PastEditionsPage';
import { PastEditionDetailPage } from './pages/public/PastEditionDetailPage';
import { EventDetailPage } from './pages/public/EventDetailPage';
import { SpeakersPage } from './pages/public/SpeakersPage';
import { SpeakerDetailPage } from './pages/public/SpeakerDetailPage';
import { GalleryDetailPage } from './pages/public/GalleryDetailPage';
import { BecomePartnerPage } from './pages/public/BecomePartnerPage';
import { MaintenancePage } from './pages/public/MaintenancePage';
import { PrivacyPage } from './pages/public/PrivacyPage';

// Admin Pages
import { LoginPage } from './pages/admin/LoginPage';
import { DashboardPage } from './pages/admin/DashboardPage';
import { EditionsAdminPage } from './pages/admin/EditionsAdminPage';
import { EventsAdminPage } from './pages/admin/EventsAdminPage';
import { SpeakersAdminPage } from './pages/admin/SpeakersAdminPage';
import { SessionsAdminPage } from './pages/admin/SessionsAdminPage';
import { TracksAdminPage } from './pages/admin/TracksAdminPage';
import { GalleriesAdminPage } from './pages/admin/GalleriesAdminPage';
import { SettingsAdminPage } from './pages/admin/SettingsAdminPage';
import { ChatbotAdminPage } from './pages/admin/ChatbotAdminPage';
import { NewsletterAdminPage } from './pages/admin/NewsletterAdminPage';
import { AdminsPage } from './pages/admin/AdminsPage';
import ImportEventPage from './pages/admin/ImportEventPage';
import ExportDbPage from './pages/admin/ExportDbPage';
import { ChallengesAdminPage } from './pages/admin/ChallengesAdminPage';
import { ChallengeVotePage } from './pages/public/ChallengeVotePage';
import { ChallengesPublicPage } from './pages/public/ChallengesPublicPage';
import { ChallengeDetailPage } from './pages/public/ChallengeDetailPage';
import { JudgeProfilePage } from './pages/public/JudgeProfilePage';

import './App.css';

// Component to apply favicon
function FaviconLoader() {
  useFavicon();
  return null;
}

function App() {
  return (
    <AuthProvider>
      <MaintenanceProvider>
        <BrowserRouter>
          <FaviconLoader />
          <ScrollToTop />
          <Routes>
            {/* Maintenance Page (always accessible) */}
            <Route path="/maintenance" element={<MaintenancePage />} />

            {/* Public Routes - Protected by MaintenanceGuard */}
            <Route element={<MaintenanceGuard><PublicLayout /></MaintenanceGuard>}>
              <Route path="/" element={<HomePage />} />
              <Route path="/editions" element={<EditionsPage />} />
              <Route path="/edizioni-precedenti" element={<PastEditionsPage />} />
              <Route path="/edizioni-precedenti/:slug" element={<PastEditionDetailPage />} />
              <Route path="/past-editions" element={<PastEditionsPage />} />
              <Route path="/past-editions/:slug" element={<PastEditionDetailPage />} />
              <Route path="/events/:slug" element={<EventDetailPage />} />
              <Route path="/speakers" element={<SpeakersPage />} />
              <Route path="/speakers/:slug" element={<SpeakerDetailPage />} />
              <Route path="/gallery/:slug" element={<GalleryDetailPage />} />
              <Route path="/diventa-partner" element={<BecomePartnerPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/challenges" element={<ChallengesPublicPage />} />
              <Route path="/challenges/judge/:slug" element={<JudgeProfilePage />} />
              <Route path="/challenges/:slug" element={<ChallengeDetailPage />} />
            </Route>

            {/* Challenge vote - standalone (no public layout) */}
            <Route path="/challenges/:slug/vote" element={<ChallengeVotePage />} />

            {/* Admin Login */}
            <Route path="/admin/login" element={<LoginPage />} />

            {/* Protected Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="editions" element={<EditionsAdminPage />} />
              <Route path="events" element={<EventsAdminPage />} />
              <Route path="speakers" element={<SpeakersAdminPage />} />
              <Route path="sessions" element={<SessionsAdminPage />} />
              <Route path="tracks" element={<TracksAdminPage />} />
              <Route path="galleries" element={<GalleriesAdminPage />} />
              <Route path="chatbot" element={<ChatbotAdminPage />} />
              <Route path="newsletter" element={<NewsletterAdminPage />} />
              <Route path="settings" element={<SettingsAdminPage />} />
              <Route path="admins" element={<AdminsPage />} />
              <Route path="import" element={<ImportEventPage />} />
              <Route path="export" element={<ExportDbPage />} />
              <Route path="challenges" element={<ChallengesAdminPage />} />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </MaintenanceProvider>
    </AuthProvider>
  );
}

export default App;
